import { Button } from "../ui/Button";
import { toCsv, toJson, type ExportMeta } from "../../engine/export";
import { t } from "../../i18n/strings";
import type { InterviewState } from "../../engine/types";

function download(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Export to community-controlled storage — the two download buttons live in the governance panel, not buried in a menu (§13). */
export function ExportBar({
  state,
  meta,
  summary,
  onExported,
}: {
  state: InterviewState;
  meta: Omit<ExportMeta, "timestamp">;
  /** The AI summary, if one was generated (Tier 3). Isolated in its own labeled column/block by export.ts — never mixed into an ordinary field (§8, §13). */
  summary?: string | null;
  onExported: () => void;
}) {
  function withTimestamp(): ExportMeta {
    return { ...meta, timestamp: new Date().toISOString() };
  }

  function exportCsv() {
    download("fieldnote_responses.csv", toCsv(state, { meta: withTimestamp(), summary }), "text/csv;charset=utf-8");
    onExported();
  }

  function exportJson() {
    download("fieldnote_interview.json", toJson(state, { meta: withTimestamp(), summary }), "application/json;charset=utf-8");
    onExported();
  }

  return (
    <div role="group" aria-label={t(state.locale, "governance.exportGroupAriaLabel")} className="flex gap-2">
      <Button variant="secondary" onClick={exportCsv}>
        {t(state.locale, "governance.downloadCsv")}
      </Button>
      <Button variant="secondary" onClick={exportJson}>
        {t(state.locale, "governance.downloadJson")}
      </Button>
    </div>
  );
}
